import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Users, Mail, Send, Sparkles, Loader2, ShoppingCart, DollarSign, Search, CheckCircle, UserCheck } from 'lucide-react';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // null=batch, object=single
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiPurpose, setAiPurpose] = useState('follow_up');
  const [aiContext, setAiContext] = useState('');

  useEffect(() => {
    api.get('/customers/enriched').then(r => setCustomers(r.data)).catch(() => toast.error('Failed to load customers'));
    setLoading(false);
  }, []);

  const filtered = customers.filter(c =>
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (email) => {
    setSelected(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const selectAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map(c => c.email));
  };

  const openSingleEmail = (customer) => {
    setEmailTarget(customer);
    setSubject('');
    setBodyHtml('');
    setAiContext('');
    setEmailDialog(true);
  };

  const openBatchEmail = () => {
    if (selected.length === 0) { toast.error('Select at least one customer'); return; }
    if (selected.length > 50) { toast.error('Maximum 50 recipients per batch'); return; }
    setEmailTarget(null);
    setSubject('');
    setBodyHtml('');
    setEmailDialog(true);
  };

  const generateAI = async () => {
    const targetEmail = emailTarget?.email || selected[0];
    if (!targetEmail) { toast.error('No customer selected'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/ai/generate-email', {
        customer_email: targetEmail,
        purpose: aiPurpose,
        context: aiContext,
      });
      setSubject(data.subject || '');
      setBodyHtml(data.body || '');
      toast.success('Email generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const sendEmail = async () => {
    if (!subject.trim() || !bodyHtml.trim()) { toast.error('Subject and body required'); return; }
    const recipients = emailTarget ? [emailTarget.email] : selected;
    if (recipients.length === 0) return;
    setSending(true);
    try {
      const wrappedHtml = `<div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 560px; margin: 0 auto; padding: 32px 24px;">
        <div style="text-align: center; margin-bottom: 24px;">
          <h1 style="font-size: 22px; color: #1A9DA5; margin: 0;">Wayfinder Guides</h1>
          <p style="color: #888; font-size: 13px; margin-top: 4px;">Audio Guides</p>
        </div>
        ${bodyHtml}
        <div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #eee; text-align: center;">
          <p style="color: #aaa; font-size: 11px;">Wayfinder Guides</p>
        </div>
      </div>`;
      const { data } = await api.post('/customers/send-email', {
        to_emails: recipients,
        subject,
        body_html: wrappedHtml,
      });
      toast.success(`Sent to ${data.sent} recipient(s)${data.failed > 0 ? `, ${data.failed} failed` : ''}`);
      setEmailDialog(false);
      setSelected([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const totalRevenue = customers.reduce((s, c) => s + (c.total_spent || 0), 0);
  const withPurchases = customers.filter(c => c.order_count > 0).length;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A9DA5]" /></div>;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Customers</h1>
          <p className="text-sm text-muted-foreground">{customers.length} customers</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button onClick={openBatchEmail} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="batch-email-btn">
              <Mail className="w-4 h-4 mr-1" />Email {selected.length} selected
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-[#1A9DA5]/10"><Users className="w-5 h-5 text-[#1A9DA5]" /></div><div><p className="text-2xl font-bold">{customers.length}</p><p className="text-xs text-muted-foreground">Total</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-emerald-500/10"><UserCheck className="w-5 h-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{withPurchases}</p><p className="text-xs text-muted-foreground">With Purchases</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-[#E8735A]/10"><ShoppingCart className="w-5 h-5 text-[#E8735A]" /></div><div><p className="text-2xl font-bold">{customers.reduce((s, c) => s + (c.order_count || 0), 0)}</p><p className="text-xs text-muted-foreground">Total Orders</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 rounded-lg bg-[#F0B429]/10"><DollarSign className="w-5 h-5 text-[#F0B429]" /></div><div><p className="text-2xl font-bold">${totalRevenue.toFixed(0)}</p><p className="text-xs text-muted-foreground">Total Revenue</p></div></CardContent></Card>
      </div>

      {/* Search + Select All */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email..." className="pl-9" data-testid="customer-search" />
        </div>
        <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
          {selected.length === filtered.length && filtered.length > 0 ? 'Deselect All' : `Select All (${filtered.length})`}
        </Button>
      </div>

      {/* Customer List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No customers found.</CardContent></Card>
        ) : filtered.map(c => (
          <Card key={c.id} className={`transition-colors ${selected.includes(c.email) ? 'border-[#1A9DA5]/50 bg-[#1A9DA5]/5' : ''}`} data-testid={`customer-row-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Checkbox checked={selected.includes(c.email)} onCheckedChange={() => toggleSelect(c.email)} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{c.email}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {c.products?.map(p => (
                      <Badge key={p.id} variant="outline" className="text-[10px] h-5">{p.title?.slice(0, 30)}{p.title?.length > 30 ? '...' : ''}</Badge>
                    ))}
                    {(!c.products || c.products.length === 0) && <span className="text-xs text-muted-foreground">No purchases</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm shrink-0">
                  <div className="text-center"><p className="font-bold">{c.order_count || 0}</p><p className="text-[10px] text-muted-foreground">Orders</p></div>
                  <div className="text-center"><p className="font-bold text-[#1A9DA5]">${(c.total_spent || 0).toFixed(0)}</p><p className="text-[10px] text-muted-foreground">Spent</p></div>
                  <div className="text-center"><p className="text-[10px] text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</p></div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openSingleEmail(c)} data-testid={`email-btn-${c.id}`}>
                  <Mail className="w-3.5 h-3.5 mr-1" />Email
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Email Dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle>
              {emailTarget ? `Email to ${emailTarget.email}` : `Email to ${selected.length} customer(s)`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* AI Generation */}
            <Card className="border-[#1A9DA5]/20 bg-[#1A9DA5]/5">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#1A9DA5]" />
                  <span className="text-sm font-semibold">AI Generate</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Purpose</label>
                    <Select value={aiPurpose} onValueChange={setAiPurpose}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="follow_up">Follow-up</SelectItem>
                        <SelectItem value="upsell">Upsell</SelectItem>
                        <SelectItem value="re_engage">Re-engage</SelectItem>
                        <SelectItem value="thank_you">Thank You</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Extra Context <span className="text-muted-foreground">(optional)</span></label>
                    <Input value={aiContext} onChange={e => setAiContext(e.target.value)} className="h-8 text-xs" placeholder="e.g. new product launch, holiday sale" />
                  </div>
                </div>
                {emailTarget && emailTarget.products?.length > 0 && (
                  <p className="text-xs text-muted-foreground">Purchased: {emailTarget.products.map(p => p.title).join(', ')}</p>
                )}
                <Button variant="outline" size="sm" onClick={generateAI} disabled={generating} className="text-[#1A9DA5]" data-testid="ai-generate-email-btn">
                  {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {generating ? 'Generating...' : 'Generate with AI'}
                </Button>
              </CardContent>
            </Card>

            <Separator />

            {/* Manual compose */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Subject</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line" data-testid="email-subject-input" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Body (HTML)</label>
              <Textarea value={bodyHtml} onChange={e => setBodyHtml(e.target.value)} rows={10} className="font-mono text-xs" placeholder="<p>Hi there,</p><p>Your email content...</p>" data-testid="email-body-input" />
              <p className="text-xs text-muted-foreground">Use HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;a href=&quot;...&quot;&gt; for formatting. A branded header and footer are added automatically.</p>
            </div>

            {/* Preview */}
            {bodyHtml && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Preview</label>
                <div className="p-4 rounded-lg border bg-white text-black text-sm" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {emailTarget ? `Sending to: ${emailTarget.email}` : `Sending to ${selected.length} recipient(s)`}
              </p>
              <Button onClick={sendEmail} disabled={sending || !subject.trim() || !bodyHtml.trim()} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="send-email-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
